import React, { useState, useCallback, useMemo } from 'react';
import { Card, Button } from '@scalingworks/react-admin-ui';
import { useTranslate, useNavigation } from '@refinedev/core';
import { Controller } from 'react-hook-form';
import {
  FaStamp,
  FaCog,
  FaInfoCircle,
  FaUserCheck,
  FaRulerCombined,
  FaTrophy,
  FaChevronLeft,
  FaChevronRight,
  FaCheck,
} from 'react-icons/fa';
import { MdCardGiftcard, MdAutoAwesome } from 'react-icons/md';
import { HiSparkles } from 'react-icons/hi';
import { useStampingSchemas } from '~/resources/loyalty/stamp-template/hooks/useStampingSchemas';
import { STAMP_CONFIG } from '~/resources/loyalty/stamp-template/config/stamp-config';
import { EligibilityCheckersManager } from '~/components/StampCard/EligibilityCheckersManager';
import { StampingRulesManager } from '~/components/StampCard/StampingRulesManager';
import { MilestoneManager } from '~/components/StampCard/MilestoneManager';
import { VisualDesignSection } from './VisualDesignSection';
import StampCardIframe from '~/components/StampCard/StampCardIframe';

interface StampTemplateFormProps {
  form: any;
  onSubmit: (data: any) => void;
  title: string;
  isUpdate?: boolean;
  loading?: boolean;
  defaultValues?: any;
}

interface Step {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  fields?: string[];
}


// Add custom styles for the range slider
const sliderStyles = `
  <style>
    input[type="range"]::-webkit-slider-thumb {
      appearance: none;
      width: 20px;
      height: 20px;
      background: #6366f1;
      cursor: pointer;
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }
    input[type="range"]::-moz-range-thumb {
      width: 20px;
      height: 20px;
      background: #6366f1;
      cursor: pointer;
      border-radius: 50%;
      border: none;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }
  </style>
`;

export const StampTemplateFormWizard: React.FC<StampTemplateFormProps> = ({
  form,
  onSubmit,
  title,
  isUpdate = false,
  loading = false,
  defaultValues = {},
}) => {
  const t = useTranslate();
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    trigger,
    reset,
    getValues,
    setValue,
  } = form;
  const allowDuplicates = watch('allowDuplicates');
  const totalStampsRequired = watch('totalStampsRequired') || 10;
  const watchedMilestones = watch('milestones'); // Explicitly watch milestones
  
  // Calculate the highest milestone position
  const getMaxMilestonePosition = () => {
    if (!watchedMilestones || !Array.isArray(watchedMilestones) || watchedMilestones.length === 0) {
      return 0;
    }
    return Math.max(...watchedMilestones.map(m => m.stampsRequired || m.milestoneStamps || 0));
  };
  
  const maxMilestonePosition = getMaxMilestonePosition();
  
  // Explicitly watch icon fields to ensure re-render
  const watchedIconUrl = watch('iconUrl');
  const watchedIconFilledUrl = watch('iconFilledUrl');
  const watchedIconWithRewardUrl = watch('iconWithRewardUrl');
  const watchedIconWithRewardFilledUrl = watch('iconWithRewardFilledUrl');
  const watchedTheme = watch('metadata.theme');

  // Watch all form values for live preview
  const formValues = watch();

  const [currentStep, setCurrentStep] = useState(1);
  const [testScenario, setTestScenario] = useState<'default' | 'almost-complete' | 'milestone-ready' | 'all-milestones' | 'near-expiry'>('default');
  const schemas = useStampingSchemas();

  // Define steps with translated labels
  const steps: Step[] = [
    {
      id: 1,
      title: t('stamp.form.steps.templateSetup.title'),
      description: t('stamp.form.steps.templateSetup.description'),
      icon: <FaInfoCircle />,
      fields: [
        'name',
        'description',
        'totalStampsRequired',
        'validityDays',
        'displayOrder',
        'maxCardsPerCustomer',
        'cardImageUrl',
        'stampImageUrl',
      ],
    },
    {
      id: 2,
      title: t('stamp.form.steps.templateSettings.title'),
      description: t('stamp.form.steps.templateSettings.description'),
      icon: <FaCog />,
      fields: ['isActive', 'autoActivate', 'allowDuplicates', 'autoCreateOnOverflow'],
    },
    {
      id: 3,
      title: t('stamp.form.steps.eligibilityRules.title'),
      description: t('stamp.form.steps.eligibilityRules.description'),
      icon: <FaUserCheck />,
      fields: ['eligibilityCheckers'],
    },
    {
      id: 4,
      title: t('stamp.form.steps.stampingRules.title'),
      description: t('stamp.form.steps.stampingRules.description'),
      icon: <FaRulerCombined />,
      fields: ['stampingRules'],
    },
    {
      id: 5,
      title: t('stamp.form.steps.milestoneRewards.title'),
      description: t('stamp.form.steps.milestoneRewards.description'),
      icon: <FaTrophy />,
      fields: ['milestones'],
    },
  ];

  // Reset form when defaultValues change (for edit mode)
  React.useEffect(() => {
    if (isUpdate && defaultValues && Object.keys(defaultValues).length > 0) {
      reset(defaultValues);
    }
  }, [defaultValues, isUpdate, reset]);

  const FormField: React.FC<{
    label: string;
    name: string;
    error?: any;
    helperText?: string;
    required?: boolean;
    children: React.ReactNode;
  }> = ({ label, name, error, helperText, required, children }) => (
    <div className="space-y-2">
      <label htmlFor={name} className="flex items-center gap-2 text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {helperText && !error && (
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <FaInfoCircle className="text-gray-400" />
          {helperText}
        </p>
      )}
      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1">
          <FaInfoCircle />
          {error.message}
        </p>
      )}
    </div>
  );

  const validateCurrentStep = async () => {
    const currentStepData = steps.find((s) => s.id === currentStep);
    if (!currentStepData?.fields) return true;

    const result = await trigger(currentStepData.fields);
    return result;
  };

  const handleNext = async () => {
    const isValid = await validateCurrentStep();
    if (isValid && currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = async (stepId: number) => {
    if (stepId < currentStep) {
      // Allow going back without validation
      setCurrentStep(stepId);
    } else if (stepId > currentStep) {
      // Validate all steps between current and target
      let canProceed = true;
      for (let i = currentStep; i < stepId; i++) {
        const stepData = steps.find((s) => s.id === i);
        if (stepData?.fields) {
          const isValid = await trigger(stepData.fields);
          if (!isValid) {
            canProceed = false;
            break;
          }
        }
      }
      if (canProceed) {
        setCurrentStep(stepId);
      }
    }
  };

  const ProgressIndicator = () => (
    <div className="flex items-center justify-between mb-8">
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-center flex-1">
          <button
            type="button"
            onClick={() => handleStepClick(step.id)}
            className={`flex flex-col items-center cursor-pointer group ${
              currentStep === step.id ? 'pointer-events-none' : ''
            }`}
          >
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                currentStep === step.id
                  ? 'bg-indigo-600 text-white shadow-lg ring-4 ring-indigo-200'
                  : currentStep > step.id
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-500 group-hover:bg-gray-300'
              }`}
            >
              {currentStep > step.id ? <FaCheck /> : <span className="text-lg">{step.icon}</span>}
            </div>
            <span
              className={`text-xs mt-2 font-medium ${
                currentStep === step.id
                  ? 'text-indigo-600'
                  : currentStep > step.id
                  ? 'text-green-600'
                  : 'text-gray-500'
              }`}
            >
              {step.title}
            </span>
          </button>
          {index < steps.length - 1 && (
            <div
              className={`flex-1 h-1 mx-2 transition-all ${
                currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Template Setup (Combined Basic Info, Rewards, and Visual Design)
        const templateName = watch('name') || '';

        const description = watch('description') || 'Collect stamps and earn rewards!';

        return (
          <div className="space-y-6">
            {/* Basic Information Section */}
            <Card className="p-6 border-0 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white">
                  <HiSparkles className="text-xl" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Basic Information</h3>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('name', {
                      required: 'Template name is required',
                      minLength: { value: 1, message: 'Name is too short' },
                      maxLength: { value: 255, message: 'Name is too long' },
                    })}
                    defaultValue={defaultValues?.name}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-400"
                    placeholder="e.g., Coffee Lover's Card"
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500 text-right">
                    {templateName.length} / 50
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Description
                  </label>
                  <textarea
                    {...register('description', {
                      maxLength: { value: 200, message: 'Description is too long' },
                    })}
                    defaultValue={defaultValues?.description}
                    rows={3}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none placeholder-gray-400"
                    placeholder="Describe what makes this loyalty program special..."
                  />
                  {errors.description && (
                    <p className="mt-1 text-xs text-red-600">{errors.description.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500 text-right">
                    {(description || '').length} / 200
                  </p>
                </div>
              </div>
            </Card>

            {/* Reward Configuration Section */}
            <Card className="p-6 border-0 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white">
                  <MdCardGiftcard className="text-xl" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">{t('stamp.rewardSettings')}</h3>
              </div>

              <div className="space-y-6">
                {/* Stamps Required with Slider */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700" title={t('stamp.totalStampsRequiredTooltip')}>
                      {t('stamp.totalStampsRequired')}
                      <span className="ml-2 text-xs text-gray-500">?</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <Controller
                        name="totalStampsRequired"
                        control={control}
                        defaultValue={defaultValues?.totalStampsRequired || 10}
                        rules={{
                          required: 'Stamps required is mandatory',
                          min: { value: Math.max(1, maxMilestonePosition), message: maxMilestonePosition > 1 ? `Minimum ${maxMilestonePosition} stamps (due to existing milestones)` : 'Minimum 1 stamp' },
                          max: { value: 15, message: 'Maximum 15 stamps' },
                        }}
                        render={({ field }) => (
                          <>
                            <input
                              type="range"
                              min={Math.max(1, maxMilestonePosition)}
                              max="15"
                              value={field.value}
                              onChange={(e) => field.onChange(parseInt(e.target.value))}
                              className="w-48 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                              style={{
                                background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${
                                  ((field.value - Math.max(1, maxMilestonePosition)) / (STAMP_CONFIG.stamps.max - Math.max(1, maxMilestonePosition))) * 100
                                }%, #e5e7eb ${
                                  ((field.value - Math.max(1, maxMilestonePosition)) / (STAMP_CONFIG.stamps.max - Math.max(1, maxMilestonePosition))) * 100
                                }%, #e5e7eb 100%)`,
                              }}
                            />
                            <div className="ml-3 px-3 py-1.5 bg-blue-600 text-white rounded-lg font-semibold min-w-[3rem] text-center">
                              {field.value}
                            </div>
                          </>
                        )}
                      />
                    </div>
                  </div>
                  {errors.totalStampsRequired && (
                    <p className="text-xs text-red-600">{errors.totalStampsRequired.message}</p>
                  )}
                  {maxMilestonePosition > 0 && (
                    <p className="text-xs text-blue-600 flex items-center gap-1">
                      <span>ℹ️</span>
                      Minimum stamps adjusted to {maxMilestonePosition} due to existing milestone at position {maxMilestonePosition}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Card Validity (Days)
                    </label>
                    <input
                      {...register('validityDays', {
                        min: { value: 1, message: 'Minimum 1 day' },
                        max: { value: 3650, message: 'Maximum 10 years' },
                      })}
                      type="number"
                      defaultValue={defaultValues?.validityDays || 90}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="90"
                    />
                    <p className="mt-1 text-xs text-gray-500">Leave empty for no expiry</p>
                    {errors.validityDays && (
                      <p className="mt-1 text-xs text-red-600">{errors.validityDays.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Cards Per Customer
                    </label>
                    <input
                      {...register('maxCardsPerCustomer', {
                        required: 'Required',
                        min: { value: 1, message: 'Minimum 1' },
                        max: { value: 100, message: 'Maximum 100' },
                      })}
                      type="number"
                      defaultValue={defaultValues?.maxCardsPerCustomer || 1}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="1"
                    />
                    <p className="mt-1 text-xs text-gray-500">Limit active cards per customer</p>
                    {errors.maxCardsPerCustomer && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.maxCardsPerCustomer.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Priority
                  </label>
                  <select
                    {...register('displayOrder')}
                    defaultValue={defaultValues?.displayOrder || 1}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
                  >
                    <option value="1">1 - Highest</option>
                    <option value="2">2 - High</option>
                    <option value="3">3 - Medium</option>
                    <option value="4">4 - Low</option>
                    <option value="5">5 - Lowest</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Visual Design Section */}
            <VisualDesignSection
              register={register}
              errors={errors}
              defaultValues={defaultValues}
              setValue={setValue}
              watch={watch}
              testScenario={testScenario}
              setTestScenario={setTestScenario}
            />
          </div>
        );

      case 2: // Template Settings
        return (
          <div className="space-y-4">
            <Controller
              name="isActive"
              control={control}
              defaultValue={defaultValues?.isActive ?? true}
              render={({ field }) => (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div>
                    <p className="font-medium text-gray-700">Template Active</p>
                    <p className="text-sm text-gray-500">Enable this template for use</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={field.value}
                      onChange={field.onChange}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              )}
            />

            <Controller
              name="autoActivate"
              control={control}
              defaultValue={defaultValues?.autoActivate ?? true}
              render={({ field }) => (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div>
                    <p className="font-medium text-gray-700">Auto-Activate Cards</p>
                    <p className="text-sm text-gray-500">New cards activate immediately</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={field.value}
                      onChange={field.onChange}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              )}
            />

            <Controller
              name="allowDuplicates"
              control={control}
              defaultValue={defaultValues?.allowDuplicates ?? false}
              render={({ field }) => (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div>
                    <p className="font-medium text-gray-700">Allow Multiple Cards</p>
                    <p className="text-sm text-gray-500">
                      Customers can have multiple active cards
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={field.value}
                      onChange={field.onChange}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              )}
            />

            <Controller
              name="autoCreateOnOverflow"
              control={control}
              defaultValue={defaultValues?.autoCreateOnOverflow ?? false}
              render={({ field }) => (
                <div
                  className={`flex items-center justify-between p-4 rounded-lg transition-colors ${
                    !allowDuplicates ? 'bg-gray-50 opacity-60' : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div>
                    <p className="font-medium text-gray-700">Auto-Create on Overflow</p>
                    <p className="text-sm text-gray-500">
                      {!allowDuplicates
                        ? 'Enable "Allow Multiple Cards" first'
                        : 'Automatically create new card when current is full'}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={field.value && allowDuplicates}
                      onChange={(e) => {
                        if (allowDuplicates) {
                          field.onChange(e.target.checked);
                        }
                      }}
                      disabled={!allowDuplicates}
                    />
                    <div
                      className={`w-11 h-6 ${
                        !allowDuplicates ? 'bg-gray-300' : 'bg-gray-200'
                      } peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600`}
                    ></div>
                  </label>
                </div>
              )}
            />

            {/* Terms and Conditions */}
            <Controller
              name="termsAndConditions"
              control={control}
              defaultValue={defaultValues?.termsAndConditions || ''}
              render={({ field }) => (
                <div className="space-y-2 mt-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Terms and Conditions
                  </label>
                  <textarea
                    {...field}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter terms and conditions for this stamp card program..."
                  />
                  <p className="text-xs text-gray-500">
                    These terms will be displayed to customers when they view their stamp card
                  </p>
                </div>
              )}
            />
          </div>
        );
      case 3: // Eligibility Rules
        return (
          <Controller
            key="eligibilityCheckers-controller"
            name="eligibilityCheckers"
            control={control}
            render={({ field }) => {
              return (
                <EligibilityCheckersManager
                  checkers={field.value || []}
                  onChange={field.onChange}
                  availableCheckers={schemas.eligibilityCheckers}
                  errors={errors?.eligibilityCheckers}
                  disabled={schemas.isLoading || loading}
                />
              );
            }}
          />
        );

      case 4: // Stamping Rules
        return (
          <Controller
            key="stampingRules-controller"
            name="stampingRules"
            control={control}
            render={({ field }) => {
              // Ensure field.value is always an array with proper structure
              const ensuredRules = Array.isArray(field.value)
                ? field.value.map((rule: any) => ({
                    ...rule,
                    checkers: Array.isArray(rule?.checkers)
                      ? rule.checkers
                      : [{ code: 'always-true', params: {} }],
                    action: rule?.action || { code: 'manual-stamps', params: { stamps: 1 } },
                    priority: rule?.priority || 1,
                  }))
                : [];

              return (
                <StampingRulesManager
                  rules={ensuredRules}
                  onChange={field.onChange}
                  availableCheckers={schemas.checkers}
                  availableActions={schemas.actions}
                  errors={errors?.stampingRules}
                  disabled={schemas.isLoading || loading}
                />
              );
            }}
          />
        );

      case 5: // Milestone Rewards
        return (
          <Controller
            key="milestones-controller"
            name="milestones"
            control={control}
            render={({ field }) => {
              return (
                <MilestoneManager
                  milestones={field.value || []}
                  onChange={field.onChange}
                  totalStampsRequired={totalStampsRequired}
                  rewardTypes={schemas.rewardTypes}
                  errors={errors?.milestones}
                  disabled={schemas.isLoading || loading}
                />
              );
            }}
          />
        );

      default:
        return null;
    }
  };

  const currentStepData = steps.find((s) => s.id === currentStep);

  return (
    <>
      <div dangerouslySetInnerHTML={{ __html: sliderStyles }} />
      <form onSubmit={handleSubmit(onSubmit)} className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-4 rounded-lg backdrop-blur-sm">
                <FaStamp className="text-3xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">{title}</h1>
                <p className="text-indigo-100 mt-1">
                  {isUpdate
                    ? 'Modify your loyalty card template settings'
                    : 'Design a new loyalty experience for your customers'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Indicator */}
        <ProgressIndicator />

        {/* Step Content with Persistent Preview */}
        <div className="flex gap-8">
          {/* Main Content - Left Side */}
          <div className="flex-1 min-h-[500px]">
            {currentStep === 1 ? (
              // Template Setup has its own layout without card wrapper
              <div className="space-y-6">{renderStepContent()}</div>
            ) : (
              // Other steps use the card wrapper
              <Card className="p-8">
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="text-indigo-600 text-2xl">{currentStepData?.icon}</div>
                    <h2 className="text-2xl font-semibold text-gray-800">
                      Step {currentStep} of {steps.length}: {currentStepData?.title}
                    </h2>
                  </div>
                  <p className="text-gray-600 ml-11">{currentStepData?.description}</p>
                </div>

                <div className="mt-8">{renderStepContent()}</div>
              </Card>
            )}
          </div>

          {/* Persistent Preview - Right Side */}
          <div className="w-96 hidden xl:block">
            <div className="sticky top-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">Live Preview</h3>
                </div>
                
                {/* Test Scenario Selector */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Test Scenarios</h4>
                  <div className="space-y-2">
                    {[
                      { key: 'default', label: 'Default (3/10)', desc: 'Normal progress' },
                      { key: 'almost-complete', label: 'Almost Complete', desc: 'One stamp away' },
                      { key: 'milestone-ready', label: 'Milestone Ready', desc: 'At milestone' },
                      { key: 'all-milestones', label: 'All Complete', desc: 'Card completed' },
                      { key: 'near-expiry', label: 'Near Expiry', desc: '70% progress' }
                    ].map((scenario) => (
                      <button
                        key={scenario.key}
                        type="button"
                        onClick={() => setTestScenario(scenario.key as any)}
                        className={`w-full text-left px-3 py-2 rounded text-xs ${
                          testScenario === scenario.key
                            ? 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                            : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        <div className="font-medium">{scenario.label}</div>
                        <div className="text-gray-500">{scenario.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Use StampCard for preview */}
                {(() => {
                  // Build mock data for StampCard based on form values
                  const stampsRequired =
                    formValues.totalStampsRequired || defaultValues?.totalStampsRequired || 10;
                  const validityDays = formValues.validityDays || defaultValues?.validityDays;
                  const milestonesRaw =
                    watchedMilestones || formValues.milestones || defaultValues?.milestones || [];
                  // Sort milestones by stamps required for proper display
                  const milestones = [...milestonesRaw].sort(
                    (a, b) =>
                      (a.stampsRequired || a.milestoneStamps || 0) -
                      (b.stampsRequired || b.milestoneStamps || 0)
                  );
                  // Calculate earned stamps based on test scenario
                  const getEarnedStamps = () => {
                    switch (testScenario) {
                      case 'almost-complete':
                        return stampsRequired - 1;
                      case 'milestone-ready':
                        return Math.floor(stampsRequired / 2);
                      case 'all-milestones':
                        return stampsRequired;
                      case 'near-expiry':
                        return Math.floor(stampsRequired * 0.7);
                      default:
                        return 3; // Show 3 stamps as filled for preview
                    }
                  };
                  const earnedStamps = getEarnedStamps();


                  // Handle both null and undefined as empty
                  const iconUrl = (watchedIconUrl === null || watchedIconUrl === '') ? undefined : (watchedIconUrl || defaultValues?.iconUrl)
                  const iconFilledUrl = (watchedIconFilledUrl === null || watchedIconFilledUrl === '') ? undefined : (watchedIconFilledUrl || defaultValues?.iconFilledUrl)
                  const iconWithRewardUrl = (watchedIconWithRewardUrl === null || watchedIconWithRewardUrl === '') ? undefined : (watchedIconWithRewardUrl || defaultValues?.iconWithRewardUrl)
                  const iconWithRewardFilledUrl = (watchedIconWithRewardFilledUrl === null || watchedIconWithRewardFilledUrl === '') ? undefined : (watchedIconWithRewardFilledUrl || defaultValues?.iconWithRewardFilledUrl)
                  // Build mock data that matches the GraphQL StampCardFieldsFragment structure
                  const mockStampCardData: any = {
                      id: 'preview',
                      currentStamps: earnedStamps,
                      totalStampsRequired: stampsRequired,
                      totalStampsEarned: earnedStamps,
                      status: 'ACTIVE',
                      activatedAt: new Date().toISOString(),
                      expiresAt: validityDays
                        ? new Date(Date.now() + validityDays * 24 * 60 * 60 * 1000).toISOString()
                        : null,
                      completedAt: earnedStamps >= stampsRequired ? new Date().toISOString() : null,
                      lastStampAt: earnedStamps > 0 ? new Date().toISOString() : null,
                      progressPercentage: Math.round((earnedStamps / stampsRequired) * 100),
                      daysRemaining: validityDays || 0,
                      isNearExpiry: false,
                      claimedMilestoneCount: milestones.filter((m: any) => (m.stampsRequired || m.milestoneStamps || 0) <= earnedStamps).length,
                      totalMilestoneCount: milestones.length,
                      template: {
                        id: 'template-preview',
                        stampingRules: []
                      },
                      stamps: Array.from({ length: stampsRequired }, (_, i) => {
                        const stampMilestones = milestones.filter((m: any) => 
                          (m.stampsRequired || m.milestoneStamps || 0) === i + 1
                        ).sort((a: any, b: any) => {
                          // Sort milestones with icons first
                          const aHasIcon = a.iconUrl && a.iconUrl !== "";
                          const bHasIcon = b.iconUrl && b.iconUrl !== "";
                          if (aHasIcon && !bHasIcon) return -1;
                          if (!aHasIcon && bHasIcon) return 1;
                          return 0;
                        });
                        
                        const firstMilestone = stampMilestones?.[0];
                        const containMilestone = stampMilestones.length > 0;

                        // Determine the appropriate icon based on state and milestone
                        let finalIcon = iconUrl; // default icon
                        if (containMilestone) {
                          if (firstMilestone?.iconUrl) {
                            finalIcon = firstMilestone.iconUrl;
                          } else if (i < earnedStamps) {
                            finalIcon = iconWithRewardFilledUrl || iconWithRewardUrl || iconFilledUrl;
                          } else {
                            finalIcon = iconWithRewardUrl || iconUrl;
                          }
                        } else if (i < earnedStamps) {
                          finalIcon = iconFilledUrl || iconUrl;
                        }

                        return {
                          index: i,
                          claimed: i < earnedStamps,
                          containMilestone: containMilestone,
                          currentIcon: finalIcon,
                          remainingStamps: Math.max(0, stampsRequired - earnedStamps),
                          milestones: stampMilestones.map((m: any) => ({
                            id: `milestone-${m.name || i}`,
                            milestoneStamps: m.stampsRequired || m.milestoneStamps || (i + 1),
                            name: m.name || `Reward ${i + 1}`,
                            description: m.description || '',
                            rewardType: m.rewardType || 'DISCOUNT',
                            rewardValue: m.rewardValue || '10',
                            autoClaimable: m.autoClaimable || false,
                            iconUrl: m.iconUrl || '',
                            badgeColor: m.badgeColor || null,
                            displayOrder: m.displayOrder || 0,
                            isClaimed: (m.stampsRequired || m.milestoneStamps || 0) <= earnedStamps,
                            isAvailable: (m.stampsRequired || m.milestoneStamps || 0) <= earnedStamps,
                            stampsRemaining: Math.max(0, (m.stampsRequired || m.milestoneStamps || 0) - earnedStamps),
                            rewardDetail: null
                          }))
                        };
                      }),
                      metadata: {
                        theme: watchedTheme,
                      }
                  };
                  
                  // Debug log to see what theme data we have
                  // Extract the theme from metadata - this is what StampCardIframe expects
                  // Fallback to undefined to let StampCardIframe use its default (Classic Coffee)
                  // Only pass theme if it has actual values
                  const theme = watchedTheme && watchedTheme.cardBackground ? watchedTheme : undefined
                  // Create a unique key based on icon values or lack thereof
                  const iconKey = `${iconUrl || 'none'}-${iconFilledUrl || 'none'}-${iconWithRewardUrl || 'none'}-${iconWithRewardFilledUrl || 'none'}`;
                  // Also create a theme key for re-rendering when theme changes
                  const themeKey = watchedTheme ? `${watchedTheme.cardBackground}-${watchedTheme.textAndBorderColor}-${watchedTheme.claimedStampColor}` : 'default';
                  
                  return (
                    <div className="transform scale-95 origin-top">
                      <StampCardIframe 
                        key={`preview-${iconKey}-${testScenario}-${themeKey}`}
                        data={mockStampCardData} 
                        theme={theme}
                      />
                    </div>
                  );
                })()}

                {/* Quick Configuration Summary */}
                <Card className="p-4 border-0 shadow-sm bg-gray-50">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Configuration Summary
                  </h4>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Template Name:</span>
                      <span className="font-medium text-gray-800">
                        {formValues.name || defaultValues?.name || 'Not set'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Stamps Required:</span>
                      <span className="font-medium text-gray-800">
                        {formValues.totalStampsRequired || defaultValues?.totalStampsRequired || 10}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Validity:</span>
                      <span className="font-medium text-gray-800">
                        {formValues.validityDays || defaultValues?.validityDays
                          ? `${formValues.validityDays || defaultValues?.validityDays} days`
                          : 'No expiry'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Max Cards:</span>
                      <span className="font-medium text-gray-800">
                        {formValues.maxCardsPerCustomer || defaultValues?.maxCardsPerCustomer || 1}{' '}
                        per customer
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status:</span>
                      <span
                        className={`font-medium ${
                          formValues.isActive ?? defaultValues?.isActive ?? true
                            ? 'text-green-600'
                            : 'text-gray-500'
                        }`}
                      >
                        {formValues.isActive ?? defaultValues?.isActive ?? true
                          ? 'Active'
                          : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Milestones:</span>
                      <span className="font-medium text-gray-800">
                        {(formValues.milestones || defaultValues?.milestones || []).length}{' '}
                        configured
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Current Step:</span>
                      <span className="font-medium text-indigo-600">{currentStepData?.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Test Scenario:</span>
                      <span className="font-medium text-purple-600 capitalize">{testScenario.replace('-', ' ')}</span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="mt-8 flex justify-between">
          <div>
            {currentStep > 1 && (
              <Button
                type="button"
                variant="default"
                size="lg"
                onClick={handlePrevious}
                className="flex items-center gap-2"
              >
                <FaChevronLeft />
                Previous
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              variant="solid"
              size="lg"
              type="submit"
              loading={loading}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            >
              <span className="flex items-center gap-2">
                {isUpdate ? (
                  <>
                    <MdAutoAwesome />
                    {t('actions.update')}
                  </>
                ) : (
                  <>
                    <MdAutoAwesome />
                    {t('actions.create')}
                  </>
                )}
              </span>
            </Button>
            
            {currentStep < steps.length && (
              <Button
                type="button"
                variant="solid"
                size="lg"
                onClick={handleNext}
                className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              >
                Next
                <FaChevronRight />
              </Button>
            )}
          </div>
        </div>
      </form>
    </>
  );
};
